// The run host — the module-scope singleton that OWNS long-running chat work:
// an agent operation, a streaming chat answer, and the draft resolution that
// follows a receipt. No React anywhere in this file.
//
// WHY THIS EXISTS (and why the page used to abort everything on unmount).
// Chat.jsx once let a run outlive the page, and that shipped a confirmed bug:
// the run kept working with NO UI anywhere, and the remount reset the
// component's `running` guard so a second run could start — two runs over the
// same 50 leads, paying twice for the same research and posting two receipts
// that disagreed. The fix then was to abort on unmount and salvage a cancelled
// receipt. This module is the fix by CONSTRUCTION instead:
//
//   • The busy guard lives HERE, at module scope, so a remount cannot forget
//     it — startOperation refuses while a run is live, and agentRun's own
//     inFlight lock (RunInFlightError) stays the hard floor underneath.
//   • A background run always has UI: getRun()/subscribeRun feed the Topbar
//     chip whenever the host is busy, so "running with no UI" cannot recur.
//   • The unmount salvage's MECHANISM (pack a cancelled receipt, write it
//     through the store) is kept — but for `pagehide`, the one exit that
//     genuinely kills the work. Navigation no longer cancels anything.
//
// Everything the host writes goes through the store-level primitives in
// chat.js (appendMessage / patchMessage / patchReceiptLead), which read the
// CURRENT store at call time and broadcast afterwards — see the ordering
// story above those primitives. The page renders live progress from host
// state while mounted; the store carries the final record.
import {
  runOperation,
  initialPasses,
  isRunInFlight,
  // The engine's own dossier merge, reused verbatim by the receipt's research
  // resolver — two implementations would be two definitions of "researched".
  researchedRowOf,
} from './agentRun.js'
import {
  streamChat,
  packRunMessage,
  // The one sentence both narrators use for "a stopped run needs no cleanup".
  // Defined beside the packer that also emits it, so the two cannot drift and
  // the receipt's dedupe still collapses them to a single line.
  NOTHING_LEFT_BEHIND,
  packResearch,
  withDraft,
  appendMessage,
  patchMessage,
  patchReceiptLead,
  loadConversations,
  flushChatSync,
} from './chat.js'
import { recordRunResult, dayKeyOf } from './dailyTasks.js'
import { logEvent } from './events.js'
// The operations on Step Functions: started, watched and turned into the
// engine's own result object here; the engine above is the fallback.
import { SERVER_OPERATION_IDS, runOperationOnServer } from './operationRuns.js'
import { runningBundleName } from './appVersion.js'
// The free workspace-cache read plus the model-written draft — the same two
// calls the receipt's resolver has always made. `refusalOf` is the wire shape
// of "the draft API declined", owned by the transport client so this module
// cannot drift into a second opinion about what a refusal is.
import { enrichLead, draftLead, refusalOf } from './enrich.js'
// The customer templates ride along for the same reason withDraft keeps the
// prospect templates: they are the FALLBACK when the model cannot write an
// inactive-user check-in or an expired win-back — never the first choice, and
// always labelled as the stand-in in their exec record.
import { humanizeDraft, inactiveUserDraft, taskFollowupDraft, expiredSubscriptionDraft } from './outreachDraft.js'
import { isEmailTouch } from './cadence.js'
import { parsePackedContacts } from './leadSearch.js'
import { greetingFor, enforceGreeting } from './greeting.js'
import { listIntel } from './intel.js'
import { keyOf as companyKeyOf } from './companyMeta.js'
// WHO A CARD IS ADDRESSED TO. The rep can switch a card to another person at
// the company (switchLeadRecipient below); these are the pure helpers that say
// who the card shows now and give each person the key their draft is kept
// under. The kept drafts live in the same per-account cache the cards use for
// their own rebuilds — a person Bo has already had an email written for costs
// nothing to come back to.
import { currentRecipientOf, recipientRecordOf, recipientKey } from './leadContacts.js'
import * as draftCache from './draftCache.js'

// ── pubsub ──────────────────────────────────────────────────────────────────
// Same idiom as copiedDrafts/dailyTasks: a listener Set, fire after every
// mutation, a broken subscriber never breaks the work.

const runListeners = new Set()
const streamListeners = new Set()

function announce(listeners) {
  for (const fn of listeners) {
    try {
      fn()
    } catch {
      /* a broken subscriber must not break the run */
    }
  }
}

/** Fires on every pass mutation and status change. Returns unsubscribe. */
export function subscribeRun(fn) {
  runListeners.add(fn)
  return () => runListeners.delete(fn)
}

/** Fires on every delta batch and status change. Returns unsubscribe. */
export function subscribeStream(fn) {
  streamListeners.add(fn)
  return () => streamListeners.delete(fn)
}

// ── module state ────────────────────────────────────────────────────────────
// `run` and `stream` keep the LAST finished work until the next start, so a
// page that remounts right after completion can still tell what just happened;
// "live" always means status === 'running' / 'streaming'.

let run = null
let stream = null

// The one-at-a-time notice, exported so the page and the tests key off the
// exact sentence rather than a re-typing of it.
export const RUN_BUSY_NOTICE = 'An operation is already running — one at a time.'
export const STREAM_BUSY_NOTICE = 'An answer is already writing — wait for it to finish.'

/** The workspace ledger's impersonation fields — same shape Chat.jsx sends. */
function impersonationData(user) {
  return user?.impersonated ? { impersonating: true, onBehalfOf: String(user.email || '') } : {}
}

// ── pagehide: the exit that really kills the work ───────────────────────────
// Navigating inside the app keeps this module alive; closing the tab does not.
// A run killed by the tab closing still owes the thread a cancelled receipt,
// and a half-written answer still owes it the honest `stopped` mark — exactly
// what the unmount salvage used to write, rehomed to the one event where it is
// still true. Everything in the handler is synchronous (appendMessage and
// patchMessage write the cache in the same tick); flushChatSync is
// fire-and-forget, and the persisted queue carries the write up on the next
// load if the network never gets its chance.

let pagehideBound = false

function ensurePagehide() {
  if (pagehideBound) return
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
  pagehideBound = true
  window.addEventListener('pagehide', onPagehide)
}

function onPagehide() {
  if (run && run.status === 'running' && !run.salvaged) {
    // Flagged BEFORE the abort: pagehide also fires on a bfcache entry, and if
    // the page comes back the engine's AbortError still unwinds through the
    // catch below — which must not append a SECOND receipt for a run this
    // handler already receipted.
    run.salvaged = true
    const halted = cancelledPasses(run.passes, 'The run was cancelled — the tab was closed mid-run')
    run.passes = halted
    appendMessage(
      run.scope,
      run.convoId,
      packRunMessage({
        operationId: run.operationId,
        // The bare catalog label, same as every other receipt of this run —
        // the scoped intent text is already in the command chip above, and two
        // receipts of one operation must not name it two different ways (the
        // receipts are the durable record, and apiTurns feeds the model from
        // them).
        operationLabel: run.opLabel,
        passes: halted,
        result: null,
      }),
    )
    flushChatSync(run.scope)
    run.controller.abort()
  }
  if (stream && stream.status === 'streaming' && !stream.salvaged) {
    stream.salvaged = true
    // The partial answer gets the same mark the Stop button writes: `stopped`
    // — a half answer the reader knows is half, never one that reads complete.
    if (stream.msgAt) {
      patchOrAppendAnswer(stream, { text: stream.text, stopped: true })
    } else if (stream.text.trim()) {
      stream.msgAt = Date.now()
      appendMessage(stream.scope, stream.convoId, {
        role: 'assistant',
        at: stream.msgAt,
        text: stream.text,
        stopped: true,
      })
    }
    flushChatSync(stream.scope)
    stream.controller.abort()
  }
}

// ── the pass graph helpers ──────────────────────────────────────────────────
// These mirror what Chat.jsx computed for its receipts (rootsOf/notStarted/
// cancelledPasses/blamePasses). They live here now because the HOST posts the
// receipts; the page keeps only rendering.

// WHAT A HALF-FINISHED RUN LEAVES BEHIND: nothing. An operation reads the
// directory and the HubSpot mirror, researches companies into the workspace
// cache and writes email drafts onto its own receipt — it creates no workspace
// object a person would have to go and find, so a run that dies mid-way needs
// no cleanup and can simply be run again. All three branches below say exactly
// that, in the shared NOTHING_LEFT_BEHIND wording imported from chat.js.

function rootsOf(passes) {
  const out = []
  passes.forEach((p, i) => {
    if (!(p.dependsOn || []).length) out.push(i)
  })
  if (out.length) return out
  return passes.length ? [0] : []
}

/**
 * A graph nothing ever ran on: the roots carry the reason, every other node is
 * marked as never reached (and `abandoned`, so none of it counts as progress).
 * Used for the refusals that happen before the engine is entered at all.
 */
function notStarted(passes, reason) {
  if (!passes.length) return passes
  const roots = new Set(rootsOf(passes))
  const endedAt = Date.now()
  return passes.map((p, i) =>
    roots.has(i)
      ? {
          ...p,
          status: 'failed',
          abandoned: false,
          headline: reason,
          detail: [NOTHING_LEFT_BEHIND],
          endedAt,
        }
      : {
          ...p,
          status: 'skipped',
          abandoned: true,
          headline: 'Not reached — the run never started',
          endedAt,
        },
  )
}

/**
 * The graph as it stood when a run was cancelled out from under it — by the
 * Stop button, or by the tab closing. The nodes that reported keep exactly
 * what they reported; the node(s) actually open take the cancellation, and
 * anything untouched is marked unreached and `abandoned` so it never counts as
 * progress. Re-running afterwards is always safe — see NOTHING_LEFT_BEHIND.
 */
function cancelledPasses(prev, reason) {
  if (!prev.length) return prev
  const live = []
  prev.forEach((p, i) => {
    if (p.status === 'running') live.push(i)
  })
  let blame = live
  if (!blame.length) {
    const next = prev.findIndex((p) => p.status === 'pending')
    blame = next >= 0 ? [next] : []
  }
  const blamed = new Set(blame)
  const endedAt = Date.now()
  return prev.map((p, i) => {
    if (blamed.has(i)) {
      return {
        ...p,
        status: 'failed',
        abandoned: false,
        headline: reason,
        detail: [`The run was cancelled part-way. ${NOTHING_LEFT_BEHIND}`],
        startedAt: p.startedAt || endedAt,
        endedAt,
      }
    }
    return p.status === 'pending'
      ? {
          ...p,
          status: 'skipped',
          abandoned: true,
          headline: 'Not reached — the run was cancelled',
          endedAt,
        }
      : p
  })
}

/**
 * Rewrite the graph after a throw, so it says where the run actually died.
 * Reads the pipeline instead of assuming it: nodes that reported themselves
 * keep their verdict, the node(s) actually in flight take the blame, and only
 * untouched nodes are marked skipped. (See Chat.jsx's original for the full
 * account of the four blame tiers — the logic is identical.)
 *
 * "What do I have to clean up before re-running?" has exactly one answer, on
 * every branch: NOTHING_LEFT_BEHIND.
 */
function blamePasses(prev, message, cancelled) {
  if (!prev.length) return prev
  const inFlight = []
  prev.forEach((p, i) => {
    if (p.status === 'running') inFlight.push(i)
  })
  const cancelledInFlight = (cancelled || []).filter((i) => Boolean(prev[i]))
  const started = prev.some((p) => p.status !== 'pending')
  const witnessed = inFlight.length > 0 || cancelledInFlight.length > 0
  let blame = inFlight.length ? inFlight : cancelledInFlight
  if (!blame.length) {
    if (!started) blame = rootsOf(prev)
    else {
      const next = prev.findIndex((p) => p.status === 'pending')
      blame = next >= 0 ? [next] : []
    }
  }
  const blamed = new Set(blame)

  if (!blame.length) {
    const last = prev.length - 1
    const next = prev.slice()
    next[last] = {
      ...next[last],
      detail: [
        ...(next[last].detail || []),
        `The run stopped after this step: ${message}`,
        NOTHING_LEFT_BEHIND,
      ].slice(0, 6),
    }
    return next
  }

  const together =
    witnessed && blame.length > 1
      ? [`${blame.length} steps were running together — the run stopped with both open.`]
      : []
  const neverBegan = witnessed ? [] : ['This step never began — the run stopped before it.']

  return prev.map((p, i) => {
    if (blamed.has(i)) {
      return {
        ...p,
        status: 'failed',
        abandoned: false,
        headline: message,
        detail: [...neverBegan, ...together, NOTHING_LEFT_BEHIND].slice(0, 4),
        startedAt: p.startedAt || Date.now(),
        endedAt: Date.now(),
      }
    }
    return p.status === 'pending'
      ? {
          ...p,
          status: 'skipped',
          abandoned: true,
          headline: 'Not reached — the run stopped earlier',
          endedAt: Date.now(),
        }
      : p
  })
}

// ── the operation ───────────────────────────────────────────────────────────

/**
 * Start one agent operation. The host owns everything from here: it posts the
 * intent message, drives agentRun.runOperation with its own AbortController,
 * posts the receipt when the run settles (success, error, abort — every branch
 * receipts), records the day's task, logs the ledger rows, and then drives the
 * receipt's draft resolution headlessly.
 *
 * @param {object}   args
 * @param {object}   args.op         the OPERATIONS catalog entry
 * @param {string}   args.convoId    the thread the run reports into
 * @param {string}   args.scope      the signed-in user's STORE scope
 * @param {object[]|Promise|function} args.rows  the lead pool, already
 *   scope-filtered by the page. A function (or promise) is awaited INSIDE the
 *   run so a directory that fails to load produces the same error receipt it
 *   always did — the intent is posted first, exactly as before.
 * @param {string}   args.category   list category key (the page's DEFAULT_CATEGORY)
 * @param {object}   args.user       the signed-in user, CAPTURED AT START. The
 *   completion ledger row reads this argument, never live auth state — an
 *   account switch mid-run must not stamp the new user onto the old user's run.
 * @param {string}   args.intentText the intent as the user voiced it
 *   ("Initial outreach — Illinois"); falls back to op.label.
 * @param {string}   [args.scopeCode] the geographic scope code for the ledger
 *   rows (data.scope), '' for an unscoped run.
 * @returns {string|null} a notice string when refused (already busy), null
 *   when the run started.
 */
export function startOperation({
  op,
  convoId,
  scope = null,
  rows,
  category = '',
  user = null,
  intentText = '',
  scopeCode = '',
} = {}) {
  if (!op || !op.id || !convoId) return 'That operation is not available.'
  // One run at a time — the module guard the remount cannot reset, with the
  // engine's own lock as the hard floor under it (a race between this check
  // and the engine still lands in the RunInFlightError branch below).
  if ((run && run.status === 'running') || isRunInFlight()) return RUN_BUSY_NOTICE
  ensurePagehide()

  const intent = String(intentText || op.label || '')
  run = {
    operationId: op.id,
    operationLabel: intent,
    opLabel: String(op.label || ''),
    convoId,
    scope,
    user,
    intent,
    scopeCode: String(scopeCode || ''),
    passes: initialPasses(op.id),
    status: 'running',
    startedAt: Date.now(),
    controller: new AbortController(),
    nodeStatus: [],
    cancelledFlight: [],
    salvaged: false,
    stoppedByUser: false,
    receiptKey: '',
  }

  // The intent, in the user's own voice, posted before any work starts — a
  // teammate opening this thread sees a COMMAND was invoked, not prose.
  appendMessage(scope, convoId, { role: 'user', kind: 'command', text: intent, operationId: op.id })
  announce(runListeners)

  // The drive owns its own failures end to end; nothing may escape as an
  // unhandled rejection out of a fire-and-forget start.
  driveOperation(op, rows, category).catch(() => {})
  return null
}

async function driveOperation(op, rows, category) {
  const r = run
  const { scope, convoId, controller, user, intent, scopeCode } = r

  // Told to the ledger BEFORE any work: a run that is later aborted, or that
  // dies with the tab, still leaves a start row, so a missing operation_run
  // reads as "started and never finished" rather than "never happened".
  logEvent('operation_started', {
    actor: user?.email || '',
    actorName: user?.name || '',
    detail: `Started ${intent}`,
    data: {
      operationId: op.id,
      convoId,
      scope: scopeCode,
      // WHICH BUILD ran it. On 2026-09-24 the rep's tab ran a day-old bundle
      // and only the wording of the ledger row gave it away; the bundle name
      // makes that a lookup (lib/appVersion.js). '' on a dev build.
      bundle: runningBundleName(),
      ...impersonationData(user),
    },
  })

  try {
    // ON THE SERVER FIRST. The operation runs as its Step Functions machine
    // (lib/operationRuns.js) and resolves the same result object the engine
    // below returns; null means it could not be started there (not deployed,
    // not reachable) and the tab runs it exactly as it always has. A server
    // run that started and then failed throws like an engine failure — it is
    // never quietly re-run here over the same leads.
    let res = SERVER_OPERATION_IDS.has(op.id)
      ? await runOperationOnServer({
          operationId: op.id,
          category,
          scopeCode,
          signal: controller.signal,
          onStarted: ({ runId }) => {
            if (run === r) r.serverRunId = runId
          },
          onPasses: (passes) => {
            if (run !== r || !Array.isArray(passes) || !passes.length) return
            r.passes = passes
            r.nodeStatus = passes.map((p) => p.status)
            announce(runListeners)
          },
        })
      : null
    if (run !== r) return
    // The resolver's own greeting step, on every lead email the machine wrote
    // (see withLeadGreeting) — the one browser step after the server that only
    // leads take, because only a lead's greeting is decided in the browser.
    if (res && Array.isArray(res.selected)) res.selected = res.selected.map(withLeadGreeting)

    if (res === null) {
      // The pool may still be loading (the page hands loadLeadRows over as a
      // function) — awaited here, inside the try, so a directory that will not
      // load blames the root nodes exactly as it always did.
      const pool = await (typeof rows === 'function' ? rows() : rows)

      res = await runOperation({
        operationId: op.id,
        rows: pool,
        category,
        signal: controller.signal,
        // The engine runs nodes CONCURRENTLY, so transitions for different
        // indices interleave. Two rules keep that lossless (unchanged from the
        // page version, where they were load-bearing):
        //
        // 1. The merge is against `r.passes`, written SYNCHRONOUSLY, so a
        //    sibling's write can never be computed from a stale copy and clobber
        //    the other branch of a fork.
        // 2. The slot is located by id, with the emitted index only as a hint,
        //    so an index that ever drifted from this module's copy of the graph
        //    would land on the right node instead of on its neighbour.
        onPass: (i, pass) => {
          if (run !== r) return // a superseded run's teardown
          const before = r.nodeStatus[i]
          r.nodeStatus[i] = pass?.status
          if (pass?.status === 'running') {
            r.cancelledFlight = r.cancelledFlight.filter((x) => x !== i)
          } else if (before === 'running' && pass?.abandoned) {
            // The engine only settles a RUNNING node with `abandoned` when it is
            // tearing the run down around it. Remember it: if this run ends in a
            // throw, this is the node the throw was actually inside.
            if (!r.cancelledFlight.includes(i)) r.cancelledFlight.push(i)
          }
          const prev = r.passes
          const at = prev[i]?.id === pass?.id ? i : prev.findIndex((p) => p.id === pass?.id)
          if (at < 0 || !prev[at] || prev[at] === pass) return
          const next = prev.slice()
          next[at] = pass
          r.passes = next
          announce(runListeners)
        },
      })
    }
    if (run !== r) return

    // The receipt is what the run leaves behind, posted into the thread. An
    // abandoned or blocked run gets one too: packRunMessage branches on
    // ok/nothingToDo and never claims work the run did not actually do.
    const message = packRunMessage({
      operationId: op.id,
      operationLabel: r.opLabel,
      passes: r.passes,
      result: res,
    })
    if (!r.salvaged) appendMessage(scope, convoId, message)

    // The day's work counts only when the run actually built something —
    // recordRunResult owns that judgement, and dailyTasks broadcasts its own
    // write, so the tiles refresh wherever they are mounted.
    const recorded = recordRunResult(op.id, res).recorded

    logEvent('operation_run', {
      actor: user?.email || '',
      actorName: user?.name || '',
      detail: `${intent}: ${
        res?.ok === true
          ? res.nothingToDo
            ? 'nothing due'
            : res.briefing
              ? `built — ${res.summary || 'briefing'}`
              : `built — ${res.selected?.length ?? 0} selected`
          : res?.nothingToDo
            ? 'nothing due (admitted)'
            : 'did not finish'
      }`,
      data: {
        operationId: op.id,
        bundle: runningBundleName(),
        convoId,
        scope: scopeCode,
        ok: res?.ok === true,
        nothingToDo: res?.nothingToDo === true,
        recorded,
        historyUnavailable: !!res?.historyUnavailable,
        inboxUnread: !!res?.inboxUnread,
        tasksUnread: !!res?.tasksUnread,
        selected: res?.selected?.length ?? 0,
        calls: res?.calls?.length ?? 0,
        waiting: res?.waiting?.length ?? 0,
        manualReply: res?.manualReply?.length ?? 0,
        tasks: res?.tasks?.length ?? 0,
        selectedKeys: (res?.selected || []).slice(0, 60).map((row) => String(companyKeyOf(row) || '')),
        batch: res?.batch || null,
        localDay: dayKeyOf(new Date()),
        ...impersonationData(user),
      },
    })

    r.status = 'done'
    announce(runListeners)

    // Then finish the job: the receipt's own drafts, resolved headlessly so
    // the fifty emails keep writing whether or not anything is mounted.
    if (!r.salvaged) {
      r.receiptKey = resolverKeyOf(scope, convoId, message.at)
      ensureDraftsResolving({ scope, convoId, msg: message })
    }
  } catch (err) {
    if (run !== r) return
    if (err?.name === 'AbortError') {
      // An aborted run is not a failure, but it is a run that did not finish —
      // the ledger gets the honest row rather than nothing.
      logEvent('operation_run', {
        actor: user?.email || '',
        actorName: user?.name || '',
        detail: `${intent}: stopped before it finished`,
        data: {
          operationId: op.id,
          bundle: runningBundleName(),
          convoId,
          scope: scopeCode,
          ok: false,
          aborted: true,
          recorded: false,
          localDay: dayKeyOf(new Date()),
          ...impersonationData(user),
        },
      })
      // The user-stop receipt. NOT on a salvaged run: pagehide already wrote
      // the cancelled receipt synchronously, and this unwind must not write a
      // second one.
      if (!r.salvaged) {
        const halted = cancelledPasses(
          r.passes,
          r.stoppedByUser ? 'You stopped the run' : 'The run was cancelled before it finished',
        )
        r.passes = halted
        appendMessage(
          scope,
          convoId,
          packRunMessage({ operationId: op.id, operationLabel: r.opLabel, passes: halted, result: null }),
        )
      }
      r.status = 'done'
    } else {
      logEvent('operation_run', {
        actor: user?.email || '',
        actorName: user?.name || '',
        detail: `${intent}: ${String(err?.message || 'the run could not start').slice(0, 200)}`,
        data: {
          operationId: op.id,
          bundle: runningBundleName(),
          convoId,
          scope: scopeCode,
          ok: false,
          recorded: false,
          error: String(
            err?.message ||
              (err?.name === 'RunInFlightError' ? 'a run was already in flight' : 'the run could not start'),
          ).slice(0, 200),
          localDay: dayKeyOf(new Date()),
          ...impersonationData(user),
        },
      })
      if (err?.name === 'RunInFlightError') {
        // The module guard should make this unreachable; the engine lock is
        // the hard floor and can still refuse (a previous abort not yet
        // unwound). Say so in the thread rather than leaving the intent
        // message hanging with nothing under it.
        const halted = notStarted(r.passes, 'A run started a moment ago is still finishing.')
        r.passes = halted
        appendMessage(
          scope,
          convoId,
          packRunMessage({ operationId: op.id, operationLabel: r.opLabel, passes: halted, result: null }),
        )
      } else {
        // Where the throw landed, read off the pipeline rather than assumed.
        const blamed = blamePasses(
          r.passes,
          err?.message || 'The run could not start.',
          r.cancelledFlight.slice(),
        )
        r.passes = blamed
        appendMessage(
          scope,
          convoId,
          packRunMessage({ operationId: op.id, operationLabel: r.opLabel, passes: blamed, result: null }),
        )
      }
      r.status = 'error'
    }
    announce(runListeners)
  }
}

/**
 * The Stop button's target: aborts a live run, and stops the draft resolution
 * of the receipt that run posted (a Stop pressed while the fifty emails are
 * still writing means "stop writing them"). Returns true when anything was
 * actually stopped.
 */
export function stopRun() {
  if (!run) return false
  let stopped = false
  if (run.status === 'running') {
    run.stoppedByUser = true
    run.controller.abort()
    stopped = true
  }
  if (run.receiptKey) {
    const token = resolvers.get(run.receiptKey)
    if (token) {
      token.alive = false
      resolvers.delete(run.receiptKey)
      stopped = true
    }
  }
  return stopped
}

/**
 * What the host is doing (or last did). `status` is 'running' while live;
 * 'done'/'error' stay readable until the next startOperation so a remounting
 * page can still see how the last run ended. `passes` is replaced wholesale on
 * every mutation and never mutated in place — safe to render from directly.
 */
export function getRun() {
  if (!run) return null
  return {
    operationId: run.operationId,
    operationLabel: run.operationLabel,
    convoId: run.convoId,
    // The STORE scope the run writes under — so the page and the Topbar chip
    // can tell a signed-in user's own work from a previous sign-in's run still
    // finishing in the background (the thread it names is not theirs to open).
    scope: run.scope,
    passes: run.passes,
    status: run.status,
    startedAt: run.startedAt,
  }
}

// ── the stream ──────────────────────────────────────────────────────────────

// How often the accumulating answer is written THROUGH to the store. A
// receipt-sized conversation must not be re-serialised per token: deltas batch
// in memory (subscribers still hear every one, for live text), and the store
// gets one write per beat plus the final on 'done'.
const STREAM_BEAT_MS = 250

// The grounding as the export keeps it: the whole context minus `pages` (the
// nav catalogue, identical on every turn), and only when it fits. Same cap and
// same honesty as the page version — a context too large to store is recorded
// as absent WITH its size, never as a half object.
const MAX_DEBUG_CHARS = 40_000

function exportableContext(context) {
  if (!context || typeof context !== 'object') return null
  const { pages, ...rest } = context
  try {
    const size = JSON.stringify(rest).length
    if (size > MAX_DEBUG_CHARS) {
      return {
        _omitted: `the grounding for this turn was ${size.toLocaleString()} characters — too large to store with the thread`,
      }
    }
    return rest
  } catch {
    return null
  }
}

/**
 * The thread as the CHAT ENDPOINT may see it. Receipts are a third kind of
 * message with no `text`; they are flattened into one honest assistant line so
 * the model can answer "what did that just build?" without being handed a role
 * it has never heard of. (Moved with the stream from Chat.jsx.)
 */
function apiTurns(messages) {
  return (messages || [])
    .map((m) => {
      if (m.role === 'user' || m.role === 'assistant') return { role: m.role, text: m.text }
      if (m.role !== 'run') return null
      const parts = [`Ran the operation “${m.operationLabel}”.`, m.title, ...(m.lines || [])]
      parts.push('Nothing was sent.')
      return { role: 'assistant', text: parts.filter(Boolean).join(' ') }
    })
    .filter(Boolean)
}

/**
 * Start (and own) one streaming answer. The page appends the user's question
 * and hands the WHOLE thread here; the host flattens it for the API, streams,
 * and writes the assistant message through the store primitives as chunks land
 * — so the answer keeps writing into the thread whether or not the page is
 * still there to watch.
 *
 * @param {object} args
 * @param {string} args.scope    the user's store scope
 * @param {string} args.convoId  the thread being answered
 * @param {object[]} args.history the thread's messages, question included
 *   (raw store shape — receipts welcome; they are flattened here)
 * @param {object} args.context  the workspace grounding for this turn
 * @returns {string|null} a notice string when refused (an answer is already
 *   writing), null when the stream started.
 */
export function startStream({ scope = null, convoId, history, context = {} } = {}) {
  if (!convoId || !Array.isArray(history) || !history.length) {
    return 'There is nothing to answer yet.'
  }
  if (stream && stream.status === 'streaming') return STREAM_BUSY_NOTICE
  ensurePagehide()
  const s = {
    convoId,
    scope,
    text: '',
    nav: [],
    status: 'streaming',
    startedAt: Date.now(),
    controller: new AbortController(),
    msgAt: 0,
    timer: null,
    salvaged: false,
    error: '',
  }
  stream = s
  announce(streamListeners)
  driveStream(s, history, context).catch(() => {})
  return null
}

/**
 * Patch the stream's answer message — or RE-APPEND it, same stamp, when the
 * patch reports the message gone. The store can be clobbered between beats:
 * the page's commit effect saves its React copy after paint, so a copy
 * snapshotted before our append can briefly paint the cache without the
 * answer, and the broadcast-scheduled merge then re-reads a cache that no
 * longer holds it (the page saves a MERGE now, but belt and braces — this is
 * the half that holds even if some other saver regresses). Dropping the false
 * return here was how an answer could silently never persist; re-appending
 * under the same `at` keeps the message's identity for the next beat and for
 * the page's live-bubble dedupe.
 */
function patchOrAppendAnswer(s, fields) {
  if (!patchMessage(s.scope, s.convoId, s.msgAt, fields)) {
    appendMessage(s.scope, s.convoId, { role: 'assistant', at: s.msgAt, ...fields })
  }
}

async function driveStream(s, history, context) {
  const turns = apiTurns(history)

  // One store write per beat. The message is APPENDED on the first beat (with
  // its `at` stamp as its identity) and PATCHED thereafter — never
  // re-serialised per token.
  const persist = () => {
    if (s.salvaged || !s.text) return
    if (!s.msgAt) {
      s.msgAt = Date.now()
      appendMessage(s.scope, s.convoId, { role: 'assistant', at: s.msgAt, text: s.text })
    } else {
      patchOrAppendAnswer(s, { text: s.text })
    }
  }
  const beat = () => {
    if (s.timer) return
    s.timer = setTimeout(() => {
      s.timer = null
      if (stream === s && s.status === 'streaming') persist()
    }, STREAM_BEAT_MS)
    if (typeof s.timer?.unref === 'function') s.timer.unref()
  }

  try {
    const { text, nav, stopped } = await streamChat({
      messages: turns,
      context,
      signal: s.controller.signal,
      onDelta: (d) => {
        if (stream !== s) return
        s.text += d
        beat()
        // Subscribers hear every delta — the mounted page's live text — while
        // the store hears only the beat above.
        announce(streamListeners)
      },
    })
    if (s.timer) {
      clearTimeout(s.timer)
      s.timer = null
    }
    const finalText = text || s.text
    s.text = finalText
    s.nav = Array.isArray(nav) ? nav : []
    if (!s.salvaged && finalText.trim()) {
      // WHAT THIS ANSWER WAS GROUNDED ON, kept with the answer — the Markdown
      // export reads it, and it has to be the object really sent for this
      // turn. Bounded, and shed first when the store runs tight (shedDebug).
      const debug = { context: exportableContext(context), at: Date.now(), turns: turns.length }
      if (!s.msgAt) {
        s.msgAt = Date.now()
        appendMessage(s.scope, s.convoId, {
          role: 'assistant',
          at: s.msgAt,
          text: finalText,
          nav: s.nav,
          stopped,
          debug,
        })
      } else {
        patchOrAppendAnswer(s, { text: finalText, nav: s.nav, stopped, debug })
      }
    }
    s.status = 'done'
  } catch (err) {
    if (s.timer) {
      clearTimeout(s.timer)
      s.timer = null
    }
    s.error = err?.message || 'Chat failed'
    // A partial the beats already persisted must not read as a finished
    // answer: it gets the same `stopped` mark a Stop press writes. (The page
    // used to discard the partial on error; keeping it, marked, is the honest
    // version now that the store may be the only copy.)
    if (!s.salvaged && s.msgAt) {
      patchOrAppendAnswer(s, { text: s.text, stopped: true })
    }
    s.status = 'error'
  }
  if (stream === s) announce(streamListeners)
}

/**
 * The stream's Stop button. streamChat resolves `stopped: true` on abort
 * rather than throwing — a partial answer the user chose to cut short is a
 * normal outcome — so the done path above lands the partial with its mark.
 */
export function stopStream() {
  if (!stream || stream.status !== 'streaming') return false
  stream.controller.abort()
  return true
}

/** The live (or last) answer. `status`: 'streaming' | 'done' | 'error'. */
export function getStream() {
  if (!stream) return null
  return {
    convoId: stream.convoId,
    scope: stream.scope,
    text: stream.text,
    nav: stream.nav,
    status: stream.status,
    startedAt: stream.startedAt,
    error: stream.error,
    msgAt: stream.msgAt || null,
  }
}

// ── draft resolution — the receipt finishes its own emails, headlessly ──────
//
// This loop used to live in LeadCards and ran only WHILE MOUNTED, so
// navigating away stalled the fifty emails being written. It is the host's
// job now: called by the host itself right after posting a receipt, and by
// the page on render for OLD receipts that still owe drafts — both routes
// deduped by the maps below.
//
// IDEMPOTENT PER (receipt at, lead key). One resolver loop per receipt (the
// `resolvers` map), one claim per lead while its wait is in flight
// (`draftClaimed`), and a settle grace per lead after its patch is handed to
// the store (`draftSettled`). The settle map subsumes the component version's
// `settled` map and the double-draft bug it fixed: a patch is applied by the
// store (and possibly re-merged away by a page save) AFTER the worker moves
// on, so on the very next pass the store can still show the old lead —
// pending, no draft — and without the grace the worker would pick it straight
// back up and pay for a second draft the write-once guard then throws away.
// After the grace, a patch that genuinely did not stick becomes eligible
// again — somebody tries again, which is the retry half of the ordering story
// in chat.js.

const resolvers = new Map() // receiptKey -> { alive }
const draftClaimed = new Set() // `${receiptKey}|${leadKey}` while a wait is in flight
const draftSettled = new Map() // `${receiptKey}|${leadKey}` -> ts the patch was handed over
const SETTLE_GRACE_MS = 30_000

function resolverKeyOf(scope, convoId, msgAt) {
  return `${scope || 'anon'}|${convoId}|${Number(msgAt)}`
}

// The receipt arrays this loop walks besides `leads`: the worklist's customer
// lanes. Their items carry draftPending/kind/key exactly like lead stubs
// (chat.js packs them that way), but they resolve under the draft route's
// fixed 'customers' category — so a worklist receipt with NO lead research
// category still arms the loop for them.
// `trialUsers` is deliberately NOT here: a trial item carries no draft and no
// draftPending — the card is facts for Bo's own pitch — so there is nothing to
// resolve, and listing the slot would only invite a write.
//
// `expiredSubscriptions` LEFT for the same reason on 2026-09-03, at the
// owner's request: the win-back email was taken off that card, so the lane
// stopped marking its items draftPending (lib/agentRun.js) and this list
// stopped offering to write them. Removing it here is what makes that true of
// receipts ALREADY STORED as well — their expired items still carry the old
// pending mark, and left in this list the resolver would go on paying for
// drafts that no card renders. It also closes the write path (applyDraft
// checks this same list), so a reply still in flight from an older run cannot
// land an email on a card that has no place to show it.
//
// `tasks` JOINED on 2026-10-09: a Daily Tasks receipt's task rows are email
// cards now (agentRun.js#judgeTaskEmails), each owed a draft written from the
// task, the thread and the rep's notes. A task row from an older receipt has
// no key and no pending mark, so owedSlot never claims it.
const CUSTOMER_SLOTS = ['inactiveUsers', 'tasks']

// One slot still owed its draft — the same three conditions the lead queue
// has always used: a pending mark, no email in the slot yet, and a key to
// claim it by.
function owedSlot(item) {
  return Boolean(
    item && (item.researchPending || item.draftPending) && !item.draft?.bodyText && item.key,
  )
}

/**
 * Every slot on one receipt still owed a draft, in receipt order: the lead
 * stubs first (only when the receipt kept its research category — without it
 * there is no cache to wait on and no lead resolver), then the customer
 * lanes, which need no category of their own.
 */
function owedSlotsOf(m, withLeads) {
  const out = []
  if (!m || typeof m !== 'object') return out
  if (withLeads && Array.isArray(m.leads)) {
    for (const l of m.leads) if (owedSlot(l)) out.push({ slot: 'leads', item: l })
  }
  for (const slot of CUSTOMER_SLOTS) {
    if (!Array.isArray(m[slot])) continue
    for (const c of m[slot]) if (owedSlot(c)) out.push({ slot, item: c })
  }
  return out
}

// Bounded on the same three axes as the component version, because sixty
// leads can be owed at once: how many are in flight, how often each is
// re-asked, and how long the whole wait may go on. On expiry the lead is
// drafted from its import row and SAYS SO.
const RESEARCH_LOOKUPS = 4
const RESEARCH_WAIT_MS = 6_000
const RESEARCH_WAIT_MAX_MS = 30_000
const RESEARCH_GIVE_UP_MS = 12 * 60 * 1000

const DRAFT_RETRY_MS = 1500
// How long drafting may keep retrying before the template stands in. Far
// shorter than the research wait: a card that spins "Writing…" for minutes is
// strictly worse than the template draft, which says in its own exec record
// why the model did not write it.
const DRAFT_GIVE_UP_MS = 60_000

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

/**
 * Ensure one receipt's pending drafts are being resolved. Safe to call any
 * number of times from any number of places — a second call while the loop is
 * live is a no-op, and a call after it drained starts a fresh loop only if
 * the store still shows leads owed (a sync merge can reopen slots).
 */
export function ensureDraftsResolving({ scope = null, convoId, msg } = {}) {
  const at = Number(msg?.at)
  const category = String(msg?.researchCategory || '').trim()
  // Armed by EITHER kind of owed slot. Lead stubs still need the receipt's
  // research category (no category, no cache, no lead resolver — unchanged);
  // the customer lanes resolve under the fixed 'customers' category, so a
  // worklist receipt that owes only those must not be turned away here.
  const owesCustomers = CUSTOMER_SLOTS.some(
    (slot) => Array.isArray(msg?.[slot]) && msg[slot].some(owedSlot),
  )
  if (!convoId || !Number.isFinite(at) || (!category && !owesCustomers)) return
  const rKey = resolverKeyOf(scope, convoId, at)
  if (resolvers.has(rKey)) return
  const token = { alive: true }
  resolvers.set(rKey, token)
  const isAlive = () => token.alive

  // What this account has previously opened, read once from the local intel
  // store — the only extra fact source, and honest by construction: a lead
  // nobody has opened simply has no snapshot.
  const snapshots = new Map()
  try {
    for (const rec of listIntel()) {
      if (rec?.id && rec.company) snapshots.set(rec.id, rec.company)
    }
  } catch {
    /* a corrupt or unavailable store just means no extra facts */
  }

  // The CURRENT store is the work queue: a lead stays in it until its patch
  // lands, and a lead resolved elsewhere (another tab, a sync merge) drops out
  // without anyone being restarted.
  const readReceipt = () => {
    const convo = loadConversations(scope).find((c) => c && c.id === convoId)
    const msgs = Array.isArray(convo?.messages) ? convo.messages : []
    for (let i = msgs.length - 1; i >= 0; i -= 1) {
      if (msgs[i]?.role === 'run' && Number(msgs[i].at) === at) return msgs[i]
    }
    return null
  }
  const takeNext = () =>
    owedSlotsOf(readReceipt(), Boolean(category)).find(
      ({ item }) =>
        !draftClaimed.has(`${rKey}|${item.key}`) &&
        !(
          draftSettled.has(`${rKey}|${item.key}`) &&
          Date.now() - draftSettled.get(`${rKey}|${item.key}`) < SETTLE_GRACE_MS
        ),
    ) || null

  const worker = async () => {
    for (;;) {
      if (!isAlive()) return
      const next = takeNext()
      if (!next) return // nothing left owed — this worker is done
      const { slot, item } = next
      const claim = `${rKey}|${item.key}`
      draftClaimed.add(claim)
      try {
        // Which resolver a slot gets is decided by the ARRAY it sits in, not
        // by sniffing the item: a lead waits on research then asks for its
        // outreach email; a customer item asks straight for its check-in or
        // win-back under the fixed 'customers' category.
        const patch =
          slot === 'leads'
            ? await resolveLeadDraft(item, snapshots.get(item.key) || null, category, isAlive)
            : await resolveCustomerDraft(item, slot, isAlive)
        // A stopped wait writes nothing; a resolved one fills the empty slot
        // through the store, which the mounted page hears about through the
        // store broadcast.
        if (patch && isAlive()) {
          draftSettled.set(claim, Date.now())
          if (slot === 'leads') {
            // FOR WHOM this draft was written, checked at the store: the rep
            // can switch the card to another person while an answer is on
            // the wire, and an email written for the previous person must
            // not land under the new one's name. A refused patch leaves the
            // slot owed; the loop re-asks for the person the card shows now.
            const wroteFor = currentRecipientOf(item).key
            const landed = patchReceiptLead(scope, convoId, at, item.key, patch, {
              forRecipient: wroteFor,
            })
            // Kept per person, so switching back to them costs nothing —
            // the same cache the cards rebuild from, keyed by receipt +
            // person. Only a draft that actually landed is worth keeping.
            if (landed && patch.draft) rememberDraftFor(at, item, wroteFor, patch.draft)
          } else patchReceiptCustomer(scope, convoId, at, slot, item.key, patch)
        }
      } finally {
        // The claim is for the duration of the wait, not forever: a patch the
        // write-once guard refused, or one a sync merge rolled back, must be
        // retryable once its grace expires.
        draftClaimed.delete(claim)
      }
    }
  }

  Promise.all(Array.from({ length: RESEARCH_LOOKUPS }, worker))
    .catch(() => {
      /* every failure inside a worker is already handled per lead */
    })
    .finally(() => {
      // Drained (or stopped): release the receipt so a LATER call can start a
      // fresh loop if the store shows slots reopened.
      if (resolvers.get(rKey) === token) resolvers.delete(rKey)
      // A patch handed to the store can still be rolled back AFTER this pool
      // drains (a concurrent page save the merge then settles the other way),
      // and the page's re-arm effect fires only when the owed COUNT changes —
      // a rollback that restores the same count re-renders nothing, so
      // "somebody tries again" was not guaranteed within the session. One
      // deferred re-check per drained loop closes that: after the settle
      // grace, re-read the receipt from the store and re-arm if slots are
      // still owed. Not on a STOPPED loop — a user's Stop means stop.
      if (!token.alive) return
      const recheck = setTimeout(() => {
        if (resolvers.has(rKey)) return
        const convo = loadConversations(scope).find((c) => c && c.id === convoId)
        const msgs = Array.isArray(convo?.messages) ? convo.messages : []
        for (let i = msgs.length - 1; i >= 0; i -= 1) {
          if (msgs[i]?.role !== 'run' || Number(msgs[i].at) !== at) continue
          // Owed slots as the STORE says them now — lead slots count only if
          // the stored receipt still carries its category (patchReceiptLead
          // clears it once no lead is waiting), customer slots always.
          const owed = owedSlotsOf(
            msgs[i],
            Boolean(String(msgs[i].researchCategory || '').trim()),
          ).length
          if (owed) ensureDraftsResolving({ scope, convoId, msg: msgs[i] })
          return
        }
      }, SETTLE_GRACE_MS + 1_000)
      if (typeof recheck?.unref === 'function') recheck.unref()
    })
}

// ── the resolver itself — moved from Chat.jsx, verbatim in behaviour ────────
// (Chat.jsx keeps only rendering + copy actions; the `alive` parameter is the
// host's per-receipt token now instead of a component effect's flag.)

/**
 * Wait for one lead's dossier. Three answers:
 *   { found: true, data }   the dossier landed
 *   { found: false }        not coming (nobody is researching it), or gave up
 *   null                    the caller stopped the wait — write nothing
 */
async function waitForDossier(key, category, alive) {
  if (!key || !category) return { found: false }
  const until = Date.now() + RESEARCH_GIVE_UP_MS
  let wait = RESEARCH_WAIT_MS
  for (;;) {
    if (!alive()) return null
    let data = null
    try {
      data = await enrichLead(category, key)
    } catch {
      // A lookup that FAILED is an unknown, never a "no dossier". Keep
      // waiting; the give-up clock is what ends this, not one bad response.
      data = null
    }
    if (!alive()) return null
    if (data && data.found && data.result) return { found: true, data }
    // The server is authoritative about whether anything is still coming.
    if (data && data.found === false && data.pending !== true) return { found: false }
    if (Date.now() >= until) return { found: false }
    await sleep(wait)
    wait = Math.min(wait * 2, RESEARCH_WAIT_MAX_MS)
  }
}

// The row facts the draft API is sent — the same fields the template read,
// plus the last-email evidence a follow-up needs. The server reads the
// dossier itself, so no research field travels here.
const DRAFT_LEAD_FIELDS = [
  'name',
  'email',
  'contact_name',
  'contact_role',
  'city_state',
  'trade',
  'service_area',
  'commercial_residential',
  'annual_volume',
  'company_size',
  'website',
]

/**
 * WHO THE EMAIL SAYS HELLO TO — the rule in lib/greeting.js, read the way the
 * resolver has it to hand:
 *   1. the rep's pick on the card's recipient dropdown;
 *   2. the stub's own `greeting`, worked out when the receipt was packed from
 *      the FULL row, packed people included (chat.js#leadStub) — the stub
 *      itself keeps no people, which is exactly how j.kly@ (Jacob
 *      Klyachenko) and edmar@ (Edward Marciszewski) came to be greeted
 *      "Hi there," and "Hi Edmar," (owner's report, 2026-10-02);
 *   3. for a receipt from before stubs carried one, the same rule over what
 *      the row and the stub still hold.
 * Returns { name, person, how }: `name` for "Hi <name>," ('' = "Hi there,"),
 * `person` the full name it belongs to ('' when only the address names them).
 */
function leadGreeting(row, lead) {
  const picked = currentRecipientOf(lead)
  if (picked.picked) {
    return greetingFor({ email: picked.email, picked: { name: picked.name, email: picked.email } })
  }
  const stored = lead?.greeting
  if (stored && typeof stored === 'object' && typeof stored.name === 'string') {
    return {
      name: String(stored.name || '').trim(),
      person: String(stored.person || '').trim(),
      how: String(stored.how || ''),
    }
  }
  return greetingFor({
    email: String(lead?.email || row?.email || '').trim().toLowerCase(),
    contactName: row?.contact_name ?? lead?.contact_name ?? '',
    people: parsePackedContacts(row?.contacts),
    company: row?.name || lead?.name || '',
    website: row?.website || lead?.website || '',
  })
}

/**
 * A lead email the machine wrote, greeted the way the resolver greets one it
 * fetched itself: the person leadGreeting picks for this row ("Hi there," when
 * the row names nobody), enforced on the text, and the exec saying the browser
 * did it. The server already enforced the same name when it knew it, so this
 * changes nothing then; when it did not, the email still opens as the chat's.
 */
function withLeadGreeting(lead) {
  const draft = lead?.draft
  if (!draft || typeof draft !== 'object' || draft.skipped || !draft.bodyText) return lead
  const greeting = leadGreeting(lead, lead)
  const post = Array.isArray(draft.exec?.post) ? draft.exec.post : []
  return {
    ...lead,
    draft: {
      ...draft,
      bodyText: enforceGreeting(draft.bodyText, greeting.name),
      exec: {
        ...(draft.exec && typeof draft.exec === 'object' ? draft.exec : { runtime: 'aws-lambda' }),
        post: [...post, { runtime: 'browser', file: 'src/lib/greeting.js', calls: ['greetingFor', 'enforceGreeting'] }],
      },
    },
  }
}

/**
 * THE NAME MUST BELONG TO THE ADDRESS — the full name that may travel as
 * `contact_name`: the person the greeting belongs to, '' when the address
 * names nobody on the row ("Hi Yvonne" to tdluzak@ still costs the reply and
 * the trust, so that case still withholds). Kept under this name because the
 * tests pin it; the rule itself is leadGreeting / lib/greeting.js.
 */
function nameForRecipient(row, lead) {
  return leadGreeting(row, lead).person
}

// The thread, as the draft API is allowed to see it: six entries, the order
// the producer gave them, subject and snippet clipped. `bounced`/`auto`
// travel because they are the difference between "they answered" and "a
// machine answered"; `from` because it makes a reply attributable.
const MAX_HISTORY_MESSAGES = 6
const MAX_HISTORY_SUBJECT = 120
const MAX_HISTORY_SNIPPET = 200

function historyForDraft(lead) {
  const rows = Array.isArray(lead?.history) ? lead.history : []
  const out = []
  for (const m of rows) {
    if (out.length >= MAX_HISTORY_MESSAGES) break
    if (!m || typeof m !== 'object') continue
    const dir = m.dir === 'in' || m.dir === 'out' ? m.dir : ''
    const at = String(m.at || '').trim()
    const subject = String(m.subject || '').trim().slice(0, MAX_HISTORY_SUBJECT)
    const snippet = String(m.snippet || '').trim().slice(0, MAX_HISTORY_SNIPPET)
    if (!dir && !subject && !snippet) continue
    const entry = {}
    if (dir) entry.dir = dir
    if (at) entry.at = at
    if (subject) entry.subject = subject
    if (snippet) entry.snippet = snippet
    const from = String(m.from || '').trim().slice(0, 160)
    if (from) entry.from = from
    if (m.bounced) entry.bounced = true
    if (m.auto) entry.auto = true
    out.push(entry)
  }
  return out
}

function draftPayloadOf(row, lead, category, kind, touch) {
  const out = {}
  for (const f of DRAFT_LEAD_FIELDS) {
    const v = row?.[f] ?? lead?.[f]
    if (v != null && String(v).trim()) out[f] = String(v).trim()
  }
  // WHO IT SAYS HELLO TO, decided once (leadGreeting) and sent twice: the
  // full name as contact_name, and the exact first name to greet as
  // greeting_name — so the writer never has to pick a first name out of
  // "J. David Rauch" or guess one from an address.
  const greeting = leadGreeting(row, lead)
  if (greeting.person) out.contact_name = greeting.person
  else delete out.contact_name
  if (greeting.name) out.greeting_name = greeting.name
  if (lead?.lastSubject) out.last_subject = String(lead.lastSubject)
  if (lead?.lastSnippet) out.last_snippet = String(lead.lastSnippet)
  // WHO THIS ACTUALLY GOES TO, said once and unambiguously, under the name the
  // route reads it by (`lead.recipient` — enrich/handler.py#_draft).
  const recipient = String(lead?.email || row?.email || '').trim()
  if (recipient) out.recipient = recipient
  // THE REP CHOSE THE PERSON. A stub carrying `recipient` (written by
  // switchLeadRecipient) overrides everything above about WHO: the name is
  // theirs by construction — it came off the row's own list of people, not
  // off a guess against the address — so nameForRecipient's veto does not
  // apply, and their title travels so the writer knows whether it is talking
  // to an estimator or a CFO. A person with no address on file gets NO
  // address at all: the writer greets them by name and the card says the
  // address is still to be found. It is never handed the row's mailbox, which
  // belongs to somebody else.
  const picked = currentRecipientOf(lead)
  if (picked.picked) {
    if (picked.name) out.contact_name = picked.name
    else delete out.contact_name
    if (picked.title) out.contact_role = picked.title
    else delete out.contact_role
    if (picked.email) {
      out.email = picked.email
      out.recipient = picked.email
    } else {
      delete out.email
      delete out.recipient
    }
  }
  const thread = historyForDraft(lead)
  // OUR OWN DOMAINS, so the route's cheapest refusal can see past its
  // constant. The set is the RUN's, carried on the stub; omitted when the stub
  // carries none.
  const ourDomains = (Array.isArray(lead?.ourDomains) ? lead.ourDomains : [])
    .map((d) => String(d || '').trim().toLowerCase().replace(/^@/, '').slice(0, 120))
    .filter(Boolean)
    .slice(0, 12)
  return {
    category,
    key: String(lead?.key || ''),
    kind,
    ...(kind === 'followup' ? { touch } : {}),
    ...(thread.length ? { thread } : {}),
    ...(ourDomains.length ? { ourDomains } : {}),
    lead: out,
  }
}

// The draft API answering 200 with NO EMAIL. The contract for a refusal is a
// 200 carrying `skip` and a `reason` and no subject/bodyText; enrich.js hands
// that over whole. THE THROW still exists for the body that is NEITHER an
// email nor a decision — recognised by its message, because that is genuinely
// all it has (a written-down coupling to enrich.js).
const NO_EMAIL_MESSAGE = 'The draft API returned no email'
const isEmptyAnswerError = (err) => !err?.status && String(err?.message || '') === NO_EMAIL_MESSAGE

// The refusal, off a 200 body — enrich.js#refusalOf decides what counts; the
// cap is the card's (a reason is rendered verbatim in place of an email).
const MAX_SKIP_REASON = 300
function skipAnswerOf(data) {
  const refused = refusalOf(data)
  if (!refused) return null
  return { reason: refused.reason.slice(0, MAX_SKIP_REASON), category: refused.category }
}

/**
 * The draft slot for a lead the API declined to write. NOT an empty draft:
 * `skipped` is what says "this emptiness is a decision" — the card renders
 * the reason and never reaches for the template, because standing the browser
 * pitch up in place of a refusal is precisely the outcome the refusal exists
 * to prevent. `decision`/`skipCategory` are what a later report counts
 * refusals by; the server's own exec wins wherever it said anything.
 */
const SKIP_REASON_PREFIX = /^no draft written\s*[:—-]\s*/i
function skippedDraft(reason, serverExec, category = '') {
  const stated = String(reason || '').trim().replace(SKIP_REASON_PREFIX, '').trim().slice(0, MAX_SKIP_REASON)
  const from = serverExec && typeof serverExec === 'object' ? serverExec : null
  const kind = String(category || '').trim().slice(0, 40)
  return {
    subject: '',
    bodyText: '',
    skipped: true,
    skipReason: stated,
    ...(kind ? { skipCategory: kind } : {}),
    exec: {
      ...(from || { runtime: 'aws-lambda', file: 'enrich/handler.py' }),
      decision: from?.decision || 'skip',
      ...(from?.skipCategory || kind ? { skipCategory: from?.skipCategory || kind } : {}),
      fallback: stated
        ? `No email was written for this lead — the draft API declined this thread: ${stated}`
        : 'No email was written for this lead — the draft API answered without an email and without a reason. Nothing was written in its place.',
    },
  }
}

/**
 * The draft call with a bounded retry. Transient answers (network / no
 * status, 429, 5xx) are re-asked with a doubling back-off; a definite answer
 * is not retried, because asking again would get the same no. 401/403 IS NOT
 * TRANSIENT — there is no session to retry into, and the template fallback
 * (labelled with the reason) is ready immediately.
 */
async function draftWithRetry(payload, alive) {
  const until = Date.now() + DRAFT_GIVE_UP_MS
  let wait = DRAFT_RETRY_MS
  for (;;) {
    try {
      return await draftLead(payload)
    } catch (err) {
      // A 200 with no email is an ANSWER, not a transport fault — retrying a
      // question already answered would spin for the whole give-up window.
      if (isEmptyAnswerError(err)) throw err
      const status = Number(err?.status) || 0
      const transient = !status || status === 429 || status >= 500
      if (!transient || Date.now() >= until || !alive()) throw err
      await sleep(wait)
      if (!alive()) throw err
      wait = Math.min(wait * 2, RESEARCH_WAIT_MAX_MS)
    }
  }
}

// One short sentence for the exec record's `fallback` — the model did not
// write this email, and this is why. The server's own words when it gave any.
function draftFailureReason(err) {
  const status = err?.status ? ` (${err.status})` : ''
  const msg = String(err?.message || 'the draft API could not be reached').slice(0, 140)
  return `POST /enrich/draft failed${status}: ${msg}`
}

// The bounded fact fields a run stamps on a lead stub (chat.js#leadStub) —
// copied onto the draft-builder input when present. The research copies are
// the RUN's story alone: an intel snapshot must never rewrite what this
// receipt's run actually held, so those fields are stripped from the snapshot
// and restored from the stub only when the run really stamped them.
const STUB_FACT_FIELDS = [
  'service_area',
  'commercial_residential',
  'annual_volume',
  'trade',
  'city_state',
  'enriched_source',
  'contact_name',
  'researched',
  'researchedAt',
  'research_sources',
  'research_source_urls',
  'research_opener',
  'research_recent_activity',
  'research_buying_signal',
  'research_current_solution',
  'research_service_area',
  'research_pain_points',
  'research_exec',
]

const PERSONALIZATION_ONLY_FIELDS = [
  'service_area',
  'commercial_residential',
  'annual_volume',
  'enriched_source',
  'researched',
  'researchMode',
  'researchedAt',
  'research_sources',
  'research_source_urls',
  'research_opener',
  'research_recent_activity',
  'research_buying_signal',
  'research_current_solution',
  'research_service_area',
  'research_pain_points',
  'research_exec',
]

// Snapshot records may carry camelCase twins.
const SNAKE_TO_CAMEL = {
  service_area: 'serviceArea',
  commercial_residential: 'commercialResidential',
  annual_volume: 'annualVolume',
  enriched_source: 'enrichedSource',
}

/**
 * The fact input one lead's draft reads: the local intel snapshot, minus the
 * personalization-only fields a snapshot may not speak for, plus the fact
 * fields this receipt's own stub carries (the stub value wins — it is what
 * THIS receipt's run actually held).
 */
function builderFacts(lead = {}, facts = null) {
  const base = { ...(typeof facts === 'object' && facts ? facts : {}) }
  for (const f of PERSONALIZATION_ONLY_FIELDS) {
    delete base[f]
    delete base[SNAKE_TO_CAMEL[f]]
  }
  for (const f of STUB_FACT_FIELDS) {
    if (lead[f] != null && lead[f] !== '') base[f] = lead[f]
  }
  return base
}

/**
 * Resolve one lead's draft and return the patch its stub should get. Every
 * terminal answer ends with the lead holding a real draft (or a refusal that
 * says why there is none) plus the exec record of what wrote it; a stopped
 * wait returns null and writes nothing.
 */
async function resolveLeadDraft(lead, facts, category, alive) {
  const key = String(lead?.key || '')
  // The person the rep switched this card to, if any — folded into the row the
  // TEMPLATE reads as well as the payload the model gets (draftPayloadOf), so
  // the fallback email, should the transport fail, greets the same person the
  // model would have. An absent address is an absent address on both paths.
  const picked = currentRecipientOf(lead)
  const pickedFacts = picked.picked
    ? {
        email: picked.email,
        contact_name: picked.name,
        ...(picked.title ? { contact_role: picked.title } : {}),
      }
    : {}
  const base = {
    ...builderFacts(lead, facts),
    name: lead.name,
    email: lead.email,
    // The thread rides on the ROW rather than on the carrier, so it reaches
    // the template builder if the transport fails and withDraft has to stand
    // in — a template email that follows a reply must not be the one place in
    // the pipeline still saying "no reply".
    ...(Array.isArray(lead?.history) && lead.history.length ? { history: lead.history } : {}),
    ...pickedFacts,
  }
  // withDraft reads the touch off the STUB to decide initial vs follow-up, so
  // the carrier it is given has to carry it.
  const touch = Number.isFinite(lead?.touch) ? lead.touch : 0
  const carrier = touch ? { touch } : {}
  const rowFor = (row) => ({ ...carrier, ...row })
  const kind = touch >= 2 && isEmailTouch(touch) ? 'followup' : 'initial'

  // 1) THE DOSSIER, when this lead is still owed one.
  let merged = rowFor(base)
  let researchPatch = {}
  if (lead?.researchPending && !lead?.draft?.bodyText) {
    const waited = await waitForDossier(key, category, alive)
    if (waited === null) return null
    if (waited.found) {
      // Merged by the ENGINE's own function, so the fields this draft reads
      // are the fields a draft written inside the run would have read.
      merged = researchedRowOf(rowFor(base), waited.data.result, {
        mode: 'cached',
        at: String(waited.data.enrichedAt || ''),
        exec: waited.data.exec || null,
      })
      researchPatch = { ...packResearch(merged), researchPending: false }
    } else {
      // The honest end state: research was started and did not arrive — never
      // the false "no web search ran on this lead".
      researchPatch = { researchPending: false, researchAttempted: true }
    }
  }
  if (!alive()) return null

  // 2) THE EMAIL — written by the model on the server. The template is the
  //    fallback, and only the fallback. Both say hello to the same person:
  //    the greeting is decided once, here, and enforced on whatever comes back.
  const greeting = leadGreeting(merged, lead)
  let draftPatch
  try {
    const data = await draftWithRetry(draftPayloadOf(merged, lead, category, kind, touch), alive)
    if (!alive()) return null
    // THE REFUSAL, before anything tries to make an email out of it: a lead
    // with no draft and the reason attached, never a template pitch.
    const skipped = skipAnswerOf(data)
    if (skipped) {
      return {
        draft: skippedDraft(skipped.reason, data.exec, skipped.category),
        ...researchPatch,
        draftPending: false,
      }
    }
    // The same de-AI-tell pass every draft gets, recorded on the exec as the
    // one browser step after the model.
    const built = humanizeDraft({
      subject: data.subject,
      bodyText: data.bodyText,
      sources: Array.isArray(data.sources) ? data.sources : [],
    })
    draftPatch = {
      draft: {
        subject: built.subject,
        // THE GREETING IS NOT THE MODEL'S CALL. It was told the name
        // (greeting_name); this makes sure the email says it, whatever the
        // writer opened with — "Hi there," over a known contact, the whole
        // name, or the company never reaches the card.
        bodyText: enforceGreeting(built.bodyText, greeting.name),
        ...(built.report ? { report: built.report } : {}),
        ...(built.sources?.length ? { sources: built.sources } : {}),
        exec: {
          ...(data.exec && typeof data.exec === 'object' ? data.exec : { runtime: 'aws-lambda' }),
          post: [
            { runtime: 'browser', file: 'src/lib/outreachDraft.js', calls: ['humanizeDraft'] },
            { runtime: 'browser', file: 'src/lib/greeting.js', calls: ['greetingFor', 'enforceGreeting'] },
          ],
        },
      },
    }
  } catch (err) {
    if (!alive()) return null
    if (isEmptyAnswerError(err)) {
      // THE ANSWER THAT IS NEITHER an email nor a decision. The template does
      // NOT stand in here: since a refusal that failed to describe itself
      // lands here too, standing up a browser-assembled pitch would sometimes
      // be sending exactly the email the server had just declined to write.
      // The rep keeps the lead and writes this one himself.
      draftPatch = { draft: skippedDraft('', null) }
    } else {
      // withDraft builds the template email onto the carrier and stamps its
      // exec with the reason the model did not write it.
      draftPatch = withDraft({ ...carrier }, { ...merged, greeting_name: greeting.name }, draftFailureReason(err))
    }
  }
  return { ...draftPatch, ...researchPatch, draftPending: false }
}

// ── switching a card to another person at the company ───────────────────────
//
// The run wrote each email to the row's primary contact. The card's recipient
// dropdown lets the rep pick somebody else on the row, and the email is then
// REWRITTEN for that person — by the same resolver, through the same slot,
// with the same refusal handling and the same template fallback — and kept,
// so coming back to anyone already written for is instant and free.
//
// STATE, and where it lives:
//   • the stub's `recipient` — the rep's pick, {name, title?, email?}. Absent
//     when the card is back on the run's own recipient, so a receipt that was
//     never switched reads exactly as it always did, on every device.
//   • the stub's `draft` — always the email for the person the card shows.
//   • the per-account draft cache (lib/draftCache.js) — every email written
//     for every person on this receipt, keyed by receipt + person. Switching
//     to someone already written for swaps their email in from here without a
//     model call. Local to this browser by design (the cache always was):
//     another device re-asks once and then has its own copy.
//
// The slot machinery is reused rather than duplicated: the switch reopens the
// slot (draft cleared, draftPending set, `recipient` written) and the
// receipt's resolver loop writes it — draftPayloadOf reads the pick, the
// store's patch is checked against it, and the settle grace that stops a
// double-draft of ONE slot is cleared because this is a NEW slot.

// The variant cache's namespace: its own, beside the card's 'd:' rebuild
// namespace, so a person's kept email and the card's template rebuild of the
// run's default can never evict each other.
const RECIPIENT_CACHE_NS = 'rcpt:'

// The signature a kept email is filed under: the receipt (a new run is a new
// draft), the person, and the touch the email was written for.
function recipientDraftSig(msgAt, lead, personKey) {
  const touch = Number.isFinite(lead?.touch) ? lead.touch : 0
  return draftCache.signature({ v: 1, receipt: Number(msgAt), person: personKey, touch })
}

function rememberDraftFor(msgAt, lead, personKey, draft) {
  const key = String(lead?.key || '')
  if (!key || !personKey || !draft || typeof draft !== 'object') return
  if (!draft.bodyText && !draft.skipped) return
  try {
    draftCache.put(`${RECIPIENT_CACHE_NS}${key}`, recipientDraftSig(msgAt, lead, personKey), draft)
  } catch {
    /* a cache that cannot be written is a cache that misses — nothing more */
  }
}

function recallDraftFor(msgAt, lead, personKey) {
  const key = String(lead?.key || '')
  if (!key || !personKey) return null
  try {
    const hit = draftCache.get(`${RECIPIENT_CACHE_NS}${key}`, recipientDraftSig(msgAt, lead, personKey))
    return hit && typeof hit === 'object' && (hit.bodyText || hit.skipped) ? hit : null
  } catch {
    return null
  }
}

function readReceiptAt(scope, convoId, at) {
  const convo = loadConversations(scope).find((c) => c && c.id === convoId)
  const msgs = Array.isArray(convo?.messages) ? convo.messages : []
  for (let i = msgs.length - 1; i >= 0; i -= 1) {
    if (msgs[i]?.role === 'run' && Number(msgs[i].at) === at) return msgs[i]
  }
  return null
}

/**
 * Switch one card to another person at the company.
 *
 * @param {object} opts
 * @param {string|null} opts.scope      the account the thread belongs to
 * @param {string} opts.convoId
 * @param {number|string} opts.msgAt    the receipt's stamp
 * @param {string} opts.leadKey
 * @param {object} opts.choice          { name, title?, email?, linkedin?, isDefault? }
 *                                      — one of lib/leadContacts.js#contactChoices
 * @param {string} [opts.category]      the lead's category, for the dossier
 *                                      read; used only when the receipt no
 *                                      longer carries its own
 * @returns {{ ok: boolean, changed?: boolean, cached?: boolean, reason?: string }}
 *   ok:false reasons: 'bad-args', 'no-lead', 'drafting' (the card is still
 *   being written — wait for it), 'no-category' (nothing can write the email:
 *   the receipt lost its category and the caller gave none).
 */
export function switchLeadRecipient({ scope = null, convoId, msgAt, leadKey, choice, category = '' } = {}) {
  const at = Number(msgAt)
  const key = String(leadKey || '')
  const id = String(convoId || '')
  const record = recipientRecordOf(choice)
  const targetKey = record ? recipientKey(record) : ''
  if (!id || !Number.isFinite(at) || !key || !targetKey) return { ok: false, reason: 'bad-args' }
  const msg = readReceiptAt(scope, id, at)
  const lead = Array.isArray(msg?.leads) ? msg.leads.find((l) => l && String(l.key || '') === key) : null
  if (!lead) return { ok: false, reason: 'no-lead' }
  const current = currentRecipientOf(lead)
  if (current.key === targetKey) return { ok: true, changed: false }
  // A card still being written cannot be switched: the answer on the wire
  // would be for the previous person, and the store guard would only refuse
  // it after the model had been paid. The card disables the control for
  // exactly this state; this is the same rule at the store.
  if (owedSlot(lead)) return { ok: false, reason: 'drafting' }

  // 1) KEEP WHAT THE CARD SHOWS NOW, filed under the person it was written
  //    for, so coming back to them is a swap and not a second model call. A
  //    refusal is kept too — it is that person's answer.
  if (lead.draft && typeof lead.draft === 'object' && (lead.draft.bodyText || lead.draft.skipped)) {
    rememberDraftFor(at, lead, current.key, lead.draft)
  }

  // 2) THE PICK. Back to the run's own recipient is stored as NO pick, so the
  //    stub reads exactly as it did before anyone switched it.
  const isDefault = Boolean(choice?.isDefault) || targetKey === currentRecipientOf({ ...lead, recipient: undefined }).key
  const recipientPatch = isDefault ? { recipient: undefined } : { recipient: record }

  // 3) ALREADY WRITTEN FOR THIS PERSON: swap it in. No pending mark, no loop,
  //    no spend — the card paints the email at once.
  const kept = recallDraftFor(at, lead, targetKey)
  if (kept) {
    patchReceiptLead(scope, id, at, key, { ...recipientPatch, draft: kept, draftPending: false }, { replace: true })
    return { ok: true, changed: true, cached: true }
  }

  // 4) NOT YET: reopen the slot for the resolver. The receipt needs a
  //    category for the dossier read (patchReceiptLead cleared it when the
  //    last draft landed); the caller's is restored when the receipt lost it.
  const cat = String(msg.researchCategory || category || '').trim().slice(0, 60)
  if (!cat) return { ok: false, reason: 'no-category' }
  const written = patchReceiptLead(
    scope,
    id,
    at,
    key,
    { ...recipientPatch, draft: undefined, draftPending: true },
    { replace: true, researchCategory: cat },
  )
  if (!written) return { ok: false, reason: 'no-lead' }
  // The settle grace stops the loop re-asking for a slot whose patch is still
  // landing. This slot was just reopened on purpose; the grace would only
  // make the rep wait up to thirty seconds for a spinner to start meaning
  // something.
  draftSettled.delete(`${resolverKeyOf(scope, id, at)}|${key}`)
  const fresh = readReceiptAt(scope, id, at)
  if (fresh) ensureDraftsResolving({ scope, convoId: id, msg: fresh })
  return { ok: true, changed: true, cached: false }
}

// ── the customer lanes' resolver — inactive users and expired win-backs ─────
//
// The worklist's customer items (chat.js packs them onto the receipt with
// draftPending/kind/key/recipient) get their email the same way lead stubs
// do: the model on the server first, the deterministic template only as the
// labelled fallback. No research wait — a customer is not researched; every
// fact the draft may state is already on the item, read from the HubSpot
// mirror by the run itself.

const CUSTOMER_KINDS = new Set(['inactive_user', 'expired_winback', 'task_followup'])

// The item facts the draft API is sent, under the snake_case names the route
// reads (enrich/handler.py#_draft, kind ∈ customer kinds). Both spellings are
// tolerated on the item because the stub is packed by another writer; every
// string is trimmed and capped here — the payload is persisted in the
// lambda's own exec record, so an unbounded field would ride along forever.
function customerDraftPayloadOf(item, kind) {
  const lead = {}
  const put = (field, value, cap = 160) => {
    if (value == null) return
    if (typeof value === 'number') {
      if (Number.isFinite(value)) lead[field] = value
      return
    }
    if (typeof value === 'boolean') {
      lead[field] = value
      return
    }
    const s = String(value).trim()
    if (s) lead[field] = s.slice(0, cap)
  }
  const num = (v) => {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  put('name', item?.name, 120)
  put('email', item?.email, 160)
  put('company', item?.company, 120)
  put('software_version', item?.softwareVersion ?? item?.software_version, 40)
  put('project_count', num(item?.projectCount ?? item?.project_count))
  put('blueprint_count', num(item?.blueprintCount ?? item?.blueprint_count))
  put('last_software_access', item?.lastSoftwareAccess ?? item?.last_software_access, 40)
  put('last_platform_login', item?.lastPlatformLogin ?? item?.last_platform_login, 40)
  put('subscription_end', item?.subscriptionEnd ?? item?.subscription_end, 40)
  put('days_inactive', num(item?.daysInactive ?? item?.days_inactive))
  put('days_expired', num(item?.daysExpired ?? item?.days_expired))
  const pay = item?.paymentMethodAttached ?? item?.payment_method_attached
  if (pay != null) lead.payment_method_attached = Boolean(pay)
  // WHO THIS GOES TO, said once — the stub's own recipient, or the address
  // the lane judged. Same rule as draftPayloadOf.
  put('recipient', item?.recipient || item?.email, 160)
  if (kind === 'task_followup') {
    // THE TASK, in the rep's own words, and what it continues: the real
    // thread (lead.history, the shape the route reads for leads too) and the
    // rep's latest notes on the company. All held facts, all bounded.
    put('task_subject', item?.subject, 120)
    put('task_due', item?.dueAt, 40)
    put('contact_role', item?.contactRole, 80)
    put('open_deal', item?.dealName, 80)
    put('trial_ends', item?.trialEnds, 40)
    put('last_subject', item?.lastSubject, 120)
    put('last_snippet', item?.lastSnippet, 160)
    const overdueDays = num(item?.overdueDays)
    if (overdueDays !== null && overdueDays > 0) lead.task_overdue_days = overdueDays
    const notes = (Array.isArray(item?.notes) ? item.notes : [])
      .map((n) => `${n?.at ? `${String(n.at).slice(0, 10)}: ` : ''}${String(n?.text || '').trim()}`)
      .filter((n) => n.trim())
      .slice(0, 2)
    if (notes.length) lead.recent_notes = notes.join(' | ').slice(0, 600)
    const history = historyForDraft(item)
    if (history.length) lead.history = history
  }
  const ourDomains = (Array.isArray(item?.ourDomains) ? item.ourDomains : [])
    .map((d) => String(d || '').trim().toLowerCase().replace(/^@/, '').slice(0, 120))
    .filter(Boolean)
    .slice(0, 12)
  return {
    category: 'customers',
    key: String(item?.key || ''),
    kind,
    ...(ourDomains.length ? { ourDomains } : {}),
    lead,
  }
}

/**
 * The template email for one customer item, with the reason the model did
 * not write it stamped on the exec — the same honesty withDraft gives the
 * prospect templates. The builders are deterministic string assembly, but the
 * try/catch stays: a slot the resolver can never fill would be re-claimed
 * after every settle grace forever, so a builder throw becomes an explicit
 * "no email, and here is why" instead of a loop.
 */
function customerTemplateDraft(item, kind, fallbackReason) {
  try {
    const built =
      kind === 'expired_winback'
        ? expiredSubscriptionDraft(item)
        : kind === 'task_followup'
          ? taskFollowupDraft(item)
          : inactiveUserDraft(item)
    return {
      subject: built.subject,
      bodyText: built.bodyText,
      exec: {
        ...(built.exec && typeof built.exec === 'object' ? built.exec : { runtime: 'browser' }),
        ...(fallbackReason ? { fallback: fallbackReason } : {}),
      },
    }
  } catch {
    return {
      subject: '',
      bodyText: '',
      skipped: true,
      skipReason: '',
      exec: {
        runtime: 'browser',
        file: 'src/lib/outreachDraft.js',
        decision: 'skip',
        fallback: `No email was written — the draft API failed (${String(fallbackReason || 'no reason recorded').slice(0, 140)}) and the template builder threw. Nothing was written in its place.`,
      },
    }
  }
}

/**
 * Resolve one customer item's draft and return the patch for its slot. Same
 * terminal shapes as resolveLeadDraft: a real draft, or a refusal that says
 * why there is none, always with the exec record of what wrote it; a stopped
 * wait returns null and writes nothing.
 */
async function resolveCustomerDraft(item, slot, alive) {
  // The kind travels on the stub; the array the item sits in is the backstop
  // for a stub packed before the kind existed — the slot cannot lie about
  // which lane the run put the item in.
  const kind = CUSTOMER_KINDS.has(item?.kind)
    ? item.kind
    : slot === 'expiredSubscriptions'
      ? 'expired_winback'
      : slot === 'tasks'
        ? 'task_followup'
        : 'inactive_user'
  let draftPatch
  try {
    const data = await draftWithRetry(customerDraftPayloadOf(item, kind), alive)
    if (!alive()) return null
    // THE REFUSAL first, exactly as for leads: a row the route judged plainly
    // not a customer to write to keeps the reason, never a template pitch.
    const skipped = skipAnswerOf(data)
    if (skipped) {
      return { draft: skippedDraft(skipped.reason, data.exec, skipped.category), draftPending: false }
    }
    // The same de-AI-tell pass every model draft gets, recorded on the exec
    // as the one browser step after the model.
    const built = humanizeDraft({
      subject: data.subject,
      bodyText: data.bodyText,
      sources: Array.isArray(data.sources) ? data.sources : [],
    })
    draftPatch = {
      draft: {
        subject: built.subject,
        bodyText: built.bodyText,
        ...(built.report ? { report: built.report } : {}),
        ...(built.sources?.length ? { sources: built.sources } : {}),
        exec: {
          ...(data.exec && typeof data.exec === 'object' ? data.exec : { runtime: 'aws-lambda' }),
          post: [{ runtime: 'browser', file: 'src/lib/outreachDraft.js', calls: ['humanizeDraft'] }],
        },
      },
    }
  } catch (err) {
    if (!alive()) return null
    if (isEmptyAnswerError(err)) {
      // Neither an email nor a decision — same rule as leads: the template
      // does NOT stand in for an answer that may have been a refusal that
      // failed to describe itself.
      draftPatch = { draft: skippedDraft('', null) }
    } else {
      draftPatch = { draft: customerTemplateDraft(item, kind, draftFailureReason(err)) }
    }
  }
  return { ...draftPatch, draftPending: false }
}

/**
 * Fill one customer slot on a run receipt — patchReceiptLead's twin for the
 * `inactiveUsers` / `expiredSubscriptions` arrays, kept HERE rather than
 * grown onto chat.js because this module is the only writer of those slots.
 * Same two rules: WRITE-ONCE (a slot holding an email is a record of what may
 * already have been copied), and the patch lands through patchMessage's
 * role-guarded store write. The read and the write happen in one synchronous
 * frame — no await between them — so a concurrent worker's write cannot be
 * clobbered by a stale array computed before it landed.
 */
function patchReceiptCustomer(scope, convoId, msgAt, slot, key, patch) {
  const id = String(convoId || '')
  const want = String(key || '')
  if (!id || !want || !CUSTOMER_SLOTS.includes(slot)) return false
  const convo = loadConversations(scope).find((c) => c && c.id === id)
  const msgs = Array.isArray(convo?.messages) ? convo.messages : []
  for (let i = msgs.length - 1; i >= 0; i -= 1) {
    const m = msgs[i]
    if (m?.role !== 'run' || Number(m.at) !== Number(msgAt)) continue
    const rows = Array.isArray(m[slot]) ? m[slot] : []
    const at = rows.findIndex((r) => r && String(r.key || '') === want)
    if (at < 0) return false
    if (rows[at].draft?.bodyText) return false // write-once
    const next = rows.slice()
    next[at] = { ...rows[at], ...patch }
    return patchMessage(scope, id, msgAt, { [slot]: next }, 'run')
  }
  return false
}

// In a browser this module is imported once at startup; bind the pagehide
// salvage immediately so a run started by ANY entry point is covered. In node
// (the tests) `window` may not exist yet at import time — ensurePagehide runs
// again on every start.
ensurePagehide()
